import classNames from 'classnames';
import { Fragment, useEffect, useState } from 'react';

import Icon, { IconName } from '@/components/Icon/Icon';
import Button from '@/components/OldButton/OldButton';
import { Contentful } from '@/contentful/types';

import useStyles from './ComparisonTableMobile.style';

export interface ComparisonTableListItem extends Contentful {
  fields: {
    icon: string;
    text: string;
  };
}

export interface ComparisonTableList extends Contentful {
  fields: {
    title: string;
    items: ComparisonTableListItem[];
  };
}

export interface ComparisonTableContent extends Contentful {
  fields: {
    title: string;
    subtitle: string;
    benefitList: ComparisonTableList[];
    ctaButtonText: string;
    ctaButtonUrl: string;
    isFavorite: boolean;
  };
}

interface Props {
  content: ComparisonTableContent[];
}

/**
 * @deprecated V2 component
 */
export default function ComparisonTableMobile({ content }: Props) {
  const { classes } = useStyles();
  const [selectedTableIndex, setSelectedTableIndex] = useState(0);

  useEffect(() => {
    const urlHash = getCleanedUrlHash();
    const foundIndex = content.findIndex(
      (item) => item.fields.title.replace(/\s/g, '').toLowerCase() === urlHash
    );
    setSelectedTableIndex(foundIndex !== -1 ? foundIndex : 0);
  }, [content]);

  const handleTableNavigation = (title: string) => {
    const urlHash = `#${title.replace(/\s/g, '').toLowerCase()}`;
    window.location.hash = urlHash;
    const foundIndex = content.findIndex(
      (item) =>
        item.fields.title.replace(/\s/g, '').toLowerCase() ===
        urlHash.substring(1)
    );
    if (foundIndex !== -1) {
      setSelectedTableIndex(foundIndex);
    }
  };

  const getCleanedUrlHash = () => {
    return window.location.hash.substring(1).replace(/\s/g, '').toLowerCase();
  };

  const renderComparisonTableNavigation = (
    tableName: string,
    index: number
  ): React.JSX.Element => {
    const urlHash = getCleanedUrlHash();
    const isCurrentTable =
      urlHash === tableName.replace(/\s/g, '').toLowerCase();
    const isDefaultTable = selectedTableIndex === 0 && !urlHash;

    const itemClass = classNames(classes.navigationItem, {
      [classes.navigationItemActive]:
        (isDefaultTable && index === 0) || isCurrentTable,
    });

    return (
      <div
        className={itemClass}
        onClick={() => handleTableNavigation(tableName)}
      >
        {tableName}
      </div>
    );
  };

  const renderTable = (tableContent: ComparisonTableContent) => {
    const content = tableContent?.fields;
    return (
      <>
        {content?.isFavorite && (
          <div className={classes.favorite}>Most Popular</div>
        )}
        <div className={classes.header}>
          <div className={classes.title}>{content?.title}</div>
          <div className={classes.subtitle}>{content?.subtitle}</div>
        </div>
        {content?.benefitList.map((item, index) => (
          <Fragment key={index}>{renderComparisonTableList(item)}</Fragment>
        ))}
        <div className={classes.cta}>
          <Button label={content?.ctaButtonText} url={content?.ctaButtonUrl} />
        </div>
      </>
    );
  };

  const renderComparisonTableList = (
    comparisonTableList: ComparisonTableList
  ): React.JSX.Element => {
    return (
      <>
        <div className={classes.benefitContainer}>
          <div className={classes.benefitTitle}>
            {comparisonTableList?.fields?.title}
          </div>
          {comparisonTableList?.fields?.items.map((item, index) => (
            <div className={classes.benefit} key={index}>
              <div className={classes.benefitIcon}>
                <Icon name={item?.fields?.icon as IconName} />
              </div>
              <div className={classes.benefitName}>{item?.fields?.text}</div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className={classes.table}>
      <div className={classes.navigation}>
        {content.map((tableContent, index) => (
          <Fragment key={index}>
            {renderComparisonTableNavigation(
              tableContent?.fields?.title,
              index
            )}
          </Fragment>
        ))}
      </div>
      <div className={classes.body}>
        {/* Render the selected table based on the selectedTableIndex */}
        {renderTable(content[selectedTableIndex])}
      </div>
    </div>
  );
}
